import { ref, useStore } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import { useAxios } from "@/composables";
import { processCoreModelResponse } from "@/store/-utils";
import CartsApi from "@/store/carts/-api";
import CartItemsApi from "@/store/carts/-cart-items-api";
import logger, { logLevel } from "@/utils/logger";
import mapData from "@/utils/transform/map-data";

export const useCartsStore = defineStore("carts", () => {
  const axios = useAxios();
  const store = useStore();

  // State
  const carts = ref<CartsMap>({});
  const cartItems = ref<CartItemsMap>({});
  const retailers = ref<RetailersMap>({});
  const retailerProductVariants = ref<RetailerProductVariantsMap>({});
  const catalogRetailerProducts = ref<CatalogRetailerProductsMap>({});
  const notification = ref<CartNotification | null>(null);
  const loggedOutAddCartItemParams = ref<PostCartItemParams | null>(null);

  // Actions
  async function fetchCarts(params: GetManyCartsParams = {}) {
    try {
      const api = new CartsApi(axios);
      const response = await api.get(params);
      carts.value = mapData("id", response.data.carts);
      cartItems.value = mapData("id", response.data.cartItems);
      retailers.value = mapData("id", response.data.retailers ?? []);
      retailerProductVariants.value = mapData("id", response.data.retailerProductVariants ?? []);
      catalogRetailerProducts.value = mapData("id", response.data.catalogRetailerProducts ?? []);
      await processCoreModelResponse(store, response.data);
    } catch (error) {
      logger(logLevel.ERROR, "Error fetching carts from the cart service", { error });
    }
  }

  async function fetchCartById(params: GetOneCartParams) {
    if (!params.cartId) {
      return;
    }
    try {
      const api = new CartsApi(axios);
      const response = await api.getOne(params);
      carts.value = Object.assign({}, carts.value, mapData("id", [response.data.cart]));
      cartItems.value = Object.assign({}, cartItems.value, mapData("id", response.data.cartItems));
      retailers.value = Object.assign({}, retailers.value, mapData("id", response.data.retailers ?? []));
      retailerProductVariants.value = Object.assign(
        {},
        retailerProductVariants.value,
        mapData("id", response.data.retailerProductVariants ?? []),
      );
      catalogRetailerProducts.value = Object.assign(
        {},
        catalogRetailerProducts.value,
        mapData("id", response.data.catalogRetailerProducts ?? []),
      );
      await processCoreModelResponse(store, response.data);
    } catch (error) {
      logger(logLevel.ERROR, "Error fetching cart from the cart service", { error });
    }
  }

  async function deleteCarts(params: DeleteCartParams) {
    notification.value = null;
    try {
      const api = new CartsApi(axios);
      await api.delete(params);
      await fetchCarts({
        includes: ["catalog_retailer_products", "product_details", "retailer_product_variants", "retailers"],
      });
    } catch (error) {
      logger(logLevel.ERROR, "Error deleting carts from the cart service", { error });
      notification.value = {
        type: "updated-error",
      };
    }
  }

  async function addCartItem(params: PostCartItemParams) {
    notification.value = null;
    let cartNotification: CartNotification | null = null;
    try {
      const api = new CartItemsApi(axios);
      const response = await api.post(params);
      carts.value = Object.assign({}, carts.value, mapData("id", [response.data.cart]));
      cartItems.value = Object.assign({}, cartItems.value, mapData("id", response.data.cartItems));
      retailers.value = Object.assign({}, retailers.value, mapData("id", response.data.retailers ?? []));
      retailerProductVariants.value = Object.assign(
        {},
        retailerProductVariants.value,
        mapData("id", response.data.retailerProductVariants ?? []),
      );
      catalogRetailerProducts.value = Object.assign(
        {},
        catalogRetailerProducts.value,
        mapData("id", response.data.catalogRetailerProducts ?? []),
      );
      await processCoreModelResponse(store, response.data);
      cartNotification = {
        type: "added",
        cartId: response.data.cart.id,
      };
    } catch (error) {
      logger(logLevel.ERROR, "Error creating cart item from the cart service", { error });
      cartNotification = {
        type: "added-error",
      };
    } finally {
      notification.value = cartNotification;
    }
  }

  async function updateCartItem(params: PatchCartItemParams) {
    notification.value = null;
    try {
      // If the new quantity exceeds the inventory quantity, then show an error and do not proceed.
      const cartItem = cartItems.value[params.cartItemId];
      const retailerProductVariantId = cartItem?.retailerProductVariantId ?? "";
      const maxQuantity = retailerProductVariants.value[retailerProductVariantId]?.quantity;
      if (maxQuantity && params.quantity > Number(maxQuantity)) {
        notification.value = {
          type: "max-error",
          cartId: cartItem?.cartId,
        };
        return;
      }

      const api = new CartItemsApi(axios);
      const response = await api.patch(params);
      carts.value = Object.assign({}, carts.value, mapData("id", [response.data.cart]));
      cartItems.value = Object.assign({}, cartItems.value, mapData("id", response.data.cartItems));
      retailers.value = Object.assign({}, retailers.value, mapData("id", response.data.retailers ?? []));
      retailerProductVariants.value = Object.assign(
        {},
        retailerProductVariants.value,
        mapData("id", response.data.retailerProductVariants ?? []),
      );
      catalogRetailerProducts.value = Object.assign(
        {},
        catalogRetailerProducts.value,
        mapData("id", response.data.catalogRetailerProducts ?? []),
      );
    } catch (error) {
      logger(logLevel.ERROR, "Error updating cart item from the cart service", { error });
      notification.value = {
        type: "updated-error",
      };
    }
  }

  async function deleteCartItem(params: DeleteCartItemParams) {
    notification.value = null;
    try {
      const api = new CartItemsApi(axios);
      await api.delete(params);
      await fetchCarts({
        includes: ["catalog_retailer_products", "product_details", "retailer_product_variants", "retailers"],
      });
    } catch (error) {
      logger(logLevel.ERROR, "Error deleting cart item from the cart service", { error });
      notification.value = {
        type: "updated-error",
      };
    }
  }

  function showCheckoutError() {
    notification.value = { type: "checkout-error" };
  }

  function resetState() {
    carts.value = {};
    cartItems.value = {};
    retailers.value = {};
    retailerProductVariants.value = {};
    catalogRetailerProducts.value = {};
  }

  return {
    carts,
    cartItems,
    retailers,
    retailerProductVariants,
    catalogRetailerProducts,
    notification,
    loggedOutAddCartItemParams,
    fetchCarts,
    fetchCartById,
    deleteCarts,
    addCartItem,
    updateCartItem,
    deleteCartItem,
    showCheckoutError,
    resetState,
  };
});
